// Board screenshots are shrunk before upload so they get through on a phone connection ("Load failed" in Safari is a
// dropped upload). A full-resolution phone screenshot saved as PNG is often 3-5 MB; as a high-quality JPEG it's well
// under 1 MB at the same size in pixels, so the text the reader sees is as sharp as before.

/** Pixels kept. Phone screenshots (about 1179x2556, 3 MP) and long scrolling ones (1179x6000) stay at full resolution;
 * bigger images are scaled down evenly, so a tall screenshot keeps its width (capping the longest side instead squeezed
 * a 1179x10000 one to 300 px wide). */
const MAX_PIXELS = 8_000_000;
/** The backend refuses any side over 12,000 px. */
const MAX_SIDE = 12_000;
/** Files this small are sent as they are. */
const SMALL_ENOUGH = 1_000_000;
const JPEG_QUALITY = 0.9;

/** The screenshot as a smaller JPEG when that helps; the original file otherwise (or if the browser can't decode it). */
export const prepareScreenshot = async (file: File): Promise<File> => {
  if (file.size <= SMALL_ENOUGH || typeof createImageBitmap !== 'function') return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(
      1,
      Math.sqrt(MAX_PIXELS / (bitmap.width * bitmap.height)),
      MAX_SIDE / Math.max(bitmap.width, bitmap.height),
    );
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
};
